import { useRef, useState } from 'react';

import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import { validateCeDocument } from '~/utils/file';
import {
  ICeDocument,
  ICeDocumentUpdate,
  ICeNewDocument,
} from '~/services/api/communityEngagement/types';
import { CE_MAX_DOCUMENTS } from '~/ui/constants/communityEngagement';

import CeDocumentStatus from '../constants/CeDocumentStatus';
import convertHeicToJpeg, {
  hasHeicSignature,
  isHeicFileName,
  toUploadFileName,
} from '../helpers/convertHeicToJpeg';
import { splitFileName } from '../helpers/formatCeDocument';
import { ICeDocumentItem } from '../types';
import { toCeDocumentItem } from '../utils/ceDocumentItem';

const useCeDocuments = (initial: ICeDocument[]) => {
  const onUploadCeDocument = useStoreActions(
    actions => actions.communityEngagement.onUploadCeDocument,
  );
  const [items, setItems] = useState<ICeDocumentItem[]>(() => initial.map(toCeDocumentItem));
  const [errors, setErrors] = useState<string[]>([]);
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const lastKey = useRef(0);
  const conversions = useRef<Promise<unknown>>(Promise.resolve());

  const replace = (key: string, next: ICeDocumentItem) =>
    setItems(current => current.map(item => (item.key === key ? next : item)));

  const toUploadFile = async (file: File): Promise<File> => {
    if (!isHeicFileName(file.name) && !(await hasHeicSignature(file))) return file;
    const converted = conversions.current.then(() => convertHeicToJpeg(file));
    conversions.current = converted.catch(() => undefined);
    return converted;
  };

  const upload = async (key: string, file: File) => {
    try {
      const { fileId, fileName } = await onUploadCeDocument(await toUploadFile(file));
      replace(key, { key, status: CeDocumentStatus.Uploaded, fileName, fileId });
    } catch (error) {
      replace(key, {
        key,
        status: CeDocumentStatus.Failed,
        fileName: toUploadFileName(file.name),
        error: extractErrorMessage(error, 'Upload failed'),
      });
    }
  };

  const addFiles = (files: File[]) => {
    const nextErrors: string[] = [];
    const valid = files.filter(file => {
      const error = validateCeDocument(file);
      if (error) nextErrors.push(error);
      return !error;
    });

    const accepted = valid.slice(0, Math.max(CE_MAX_DOCUMENTS - items.length, 0));
    const leftOut = valid.length - accepted.length;
    if (leftOut > 0) {
      nextErrors.push(
        `Up to ${CE_MAX_DOCUMENTS} documents can be attached; ${leftOut} ${
          leftOut === 1 ? 'file was' : 'files were'
        } not added.`,
      );
    }
    setErrors(nextErrors);

    const added = accepted.map(file => {
      lastKey.current += 1;
      return { key: `new-${lastKey.current}`, file };
    });
    setItems(current => [
      ...current,
      ...added.map(
        ({ key, file }): ICeDocumentItem => ({
          key,
          status: CeDocumentStatus.Uploading,
          fileName: toUploadFileName(file.name),
        }),
      ),
    ]);
    added.forEach(({ key, file }) => upload(key, file));
  };

  const remove = (key: string) => setItems(current => current.filter(item => item.key !== key));

  const getRenameError = (fileName: string): string | null =>
    splitFileName(fileName).name ? null : 'File name is required';

  const rename = (key: string, fileName: string) => {
    setItems(current => current.map(item => (item.key === key ? { ...item, fileName } : item)));
    setRenamingKey(null);
  };

  const toCreateRequest = (): ICeNewDocument[] =>
    items.flatMap(item =>
      item.status === CeDocumentStatus.Uploaded
        ? [{ fileId: item.fileId, fileName: item.fileName }]
        : [],
    );

  const toUpdateRequest = (): ICeDocumentUpdate[] =>
    items.flatMap((item): ICeDocumentUpdate[] => {
      if (item.status === CeDocumentStatus.Existing) return [{ id: item.document.id }];
      if (item.status === CeDocumentStatus.Uploaded)
        return [{ fileId: item.fileId, fileName: item.fileName }];
      return [];
    });

  return {
    items,
    errors,
    isFull: items.length >= CE_MAX_DOCUMENTS,
    isBusy:
      renamingKey !== null ||
      items.some(
        item =>
          item.status === CeDocumentStatus.Uploading || item.status === CeDocumentStatus.Failed,
      ),
    isDirty:
      items.length !== initial.length ||
      items.some(item => item.status !== CeDocumentStatus.Existing),
    renamingKey,
    addFiles,
    remove,
    startRename: (key: string) => setRenamingKey(key),
    cancelRename: () => setRenamingKey(null),
    getRenameError,
    rename,
    toCreateRequest,
    toUpdateRequest,
  };
};

export type ICeDocuments = ReturnType<typeof useCeDocuments>;

export default useCeDocuments;
